import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { FadeIn, Stagger, StaggerItem } from '@/components/motion/FadeIn';
import { MaskReveal } from '@/components/motion/Reveal';
import { usePosts } from '@/lib/cms';
import { formatDate } from '@/lib/utils';

interface Props {
  headline: string;
  count: number;
  view_all_label: string;
  view_all_href: string;
}

export function HomeLatestPosts({ headline, count, view_all_label, view_all_href }: Props) {
  const { data: posts = [], isLoading } = usePosts(count);
  return (
    <MaskReveal className="section-y-lg" tilt={-1.2} duration={1.2}>
      <div className="container-wide">
        <FadeIn>
          <div className="flex items-end justify-between gap-6">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">Insights</p>
              <h2 className="mt-3 text-h2 text-fg">{headline}</h2>
            </div>
            <Link
              to={view_all_href}
              className="hidden items-center gap-1 text-sm text-gold-500 hover:underline md:inline-flex"
            >
              {view_all_label}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </FadeIn>

        <Stagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {isLoading
            ? Array.from({ length: count }).map((_, i) => (
                <StaggerItem key={`p-${i}`}>
                  <Card className="h-full animate-pulse overflow-hidden">
                    <div className="aspect-video w-full bg-bg-subtle" />
                    <CardHeader>
                      <div className="h-5 w-3/4 rounded bg-bg-subtle" />
                      <div className="mt-2 h-4 w-full rounded bg-bg-subtle" />
                    </CardHeader>
                  </Card>
                </StaggerItem>
              ))
            : posts.length === 0
              ? (
                <StaggerItem>
                  <Card className="col-span-full p-10 text-center text-sm text-fg-subtle">
                    No posts published yet. Write one from the admin portal.
                  </Card>
                </StaggerItem>
              )
              : posts.map((post) => (
                  <StaggerItem key={post.id}>
                    <Link to={`/blog/${post.slug}`} className="group block h-full">
                      <Card className="h-full overflow-hidden transition-all hover:-translate-y-1 hover:border-gold-500/40 hover:shadow-gold">
                        <div className="relative aspect-video w-full overflow-hidden bg-gradient-to-br from-gold-500/20 to-bg-elevated">
                          {post.hero_image_url ? (
                            <img
                              src={post.hero_image_url}
                              alt={`${post.title} hero`}
                              loading="lazy"
                              decoding="async"
                              className="h-full w-full object-cover transition-transform duration-slow group-hover:scale-105"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center text-xs uppercase tracking-widest text-fg-subtle">
                              {post.category}
                            </div>
                          )}
                        </div>
                        <CardHeader>
                          <div className="flex items-center gap-2">
                            <Badge variant="gold">{post.category}</Badge>
                            {post.published_at && (
                              <span className="text-xs text-fg-subtle">
                                {formatDate(post.published_at)}
                              </span>
                            )}
                          </div>
                          <CardTitle className="mt-3 line-clamp-2">{post.title}</CardTitle>
                          <CardDescription className="line-clamp-2">{post.excerpt}</CardDescription>
                        </CardHeader>
                      </Card>
                    </Link>
                  </StaggerItem>
                ))}
        </Stagger>
      </div>
    </MaskReveal>
  );
}